import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { apiClient } from '../../services/api';

interface TenantConfig {
  salonId: string;
  tenantId: string;
  subscriptionTier: 'basic' | 'professional' | 'enterprise';
  features: Record<string, boolean>;
  limits: {
    maxUsers: number;
    maxCustomers: number;
    maxAppointments: number;
    storageQuota: number;
  };
  customDomain?: string;
}

interface UsageLimits {
  users: { current: number; max: number; withinLimit: boolean };
  customers: { current: number; max: number; withinLimit: boolean };
  appointments: { current: number; max: number; withinLimit: boolean };
  storage: { current: number; max: number; withinLimit: boolean };
}

interface SalonConfigurationProps {
  salonId: string;
  onClose: () => void;
}

const SalonConfiguration: React.FC<SalonConfigurationProps> = ({ salonId, onClose }) => {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [limits, setLimits] = useState<UsageLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchConfiguration();
  }, [salonId]);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/tenants/${salonId}/config`);
      
      if (response.success) {
        setConfig(response.data.config);
        setLimits(response.data.limits);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!config) return;

    try {
      setSaving(true);
      const response = await apiClient.put(`/tenants/${salonId}/config`, {
        subscriptionTier: config.subscriptionTier,
        maxUsers: config.limits.maxUsers,
        maxCustomers: config.limits.maxCustomers,
        maxAppointments: config.limits.maxAppointments,
        storageQuota: config.limits.storageQuota,
        customDomain: config.customDomain,
        features: config.features
      });
      
      if (response.success) {
        setSuccess('Configuration updated successfully');
        fetchConfiguration(); // Refresh data
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (updates: Partial<TenantConfig>) => {
    if (config) {
      setConfig({ ...config, ...updates });
    }
  };

  const updateLimits = (limits: Partial<TenantConfig['limits']>) => {
    if (config) {
      setConfig({
        ...config,
        limits: { ...config.limits, ...limits }
      });
    }
  };

  const updateFeature = (feature: string, enabled: boolean) => {
    if (config) {
      setConfig({
        ...config,
        features: { ...config.features, [feature]: enabled }
      });
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUsagePercentage = (current: number, max: number) => {
    return Math.min((current / max) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading configuration...</Typography>
      </Box>
    );
  }

  if (!config || !limits) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load configuration</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Salon Configuration</Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchConfiguration}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveConfiguration}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<BusinessIcon />} label="Basic Settings" />
          <Tab icon={<SettingsIcon />} label="Features & Limits" />
          <Tab icon={<SecurityIcon />} label="Security & Access" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<PaymentIcon />} label="Payment Settings" />
          <Tab icon={<AnalyticsIcon />} label="Analytics & Reports" />
        </Tabs>
      </Box>

      {/* Basic Settings Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Basic Configuration */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Configuration
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Subscription Tier</InputLabel>
                      <Select
                        value={config?.subscriptionTier || 'basic'}
                        onChange={(e) => updateConfig({ 
                          subscriptionTier: e.target.value as TenantConfig['subscriptionTier'] 
                        })}
                      >
                        <MenuItem value="basic">Basic</MenuItem>
                        <MenuItem value="professional">Professional</MenuItem>
                        <MenuItem value="enterprise">Enterprise</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Custom Domain"
                      value={config?.customDomain || ''}
                      onChange={(e) => updateConfig({ customDomain: e.target.value || undefined })}
                      placeholder="salon.yourdomain.com"
                      helperText="Optional custom domain for this salon"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Limits */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Usage Limits
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Max Users"
                      type="number"
                      value={config?.limits.maxUsers || 5}
                      onChange={(e) => updateLimits({ maxUsers: parseInt(e.target.value) })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Max Customers"
                      type="number"
                      value={config?.limits.maxCustomers || 1000}
                      onChange={(e) => updateLimits({ maxCustomers: parseInt(e.target.value) })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Max Appointments"
                      type="number"
                      value={config?.limits.maxAppointments || 10000}
                      onChange={(e) => updateLimits({ maxAppointments: parseInt(e.target.value) })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Storage Quota (GB)"
                      type="number"
                      value={config ? Math.round(config.limits.storageQuota / (1024 * 1024 * 1024)) : 5}
                      onChange={(e) => updateLimits({ 
                        storageQuota: parseInt(e.target.value) * 1024 * 1024 * 1024 
                      })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Current Usage */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Usage
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Users
                      </Typography>
                      <Typography variant="h4">
                        {limits?.users.current || 0}/{limits?.users.max || 0}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={limits ? getUsagePercentage(limits.users.current, limits.users.max) : 0}
                        color={limits ? getUsageColor(getUsagePercentage(limits.users.current, limits.users.max)) : 'primary'}
                        sx={{ mt: 1 }}
                      />
                      {limits && !limits.users.withinLimit && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Over Limit"
                          color="error"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Customers
                      </Typography>
                      <Typography variant="h4">
                        {limits?.customers.current || 0}/{limits?.customers.max || 0}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={limits ? getUsagePercentage(limits.customers.current, limits.customers.max) : 0}
                        color={limits ? getUsageColor(getUsagePercentage(limits.customers.current, limits.customers.max)) : 'primary'}
                        sx={{ mt: 1 }}
                      />
                      {limits && !limits.customers.withinLimit && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Over Limit"
                          color="error"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Appointments
                      </Typography>
                      <Typography variant="h4">
                        {limits?.appointments.current || 0}/{limits?.appointments.max || 0}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={limits ? getUsagePercentage(limits.appointments.current, limits.appointments.max) : 0}
                        color={limits ? getUsageColor(getUsagePercentage(limits.appointments.current, limits.appointments.max)) : 'primary'}
                        sx={{ mt: 1 }}
                      />
                      {limits && !limits.appointments.withinLimit && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Over Limit"
                          color="error"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Storage
                      </Typography>
                      <Typography variant="h4">
                        {limits ? formatBytes(limits.storage.current) : '0 B'}/{limits ? formatBytes(limits.storage.max) : '0 B'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={limits ? getUsagePercentage(limits.storage.current, limits.storage.max) : 0}
                        color={limits ? getUsageColor(getUsagePercentage(limits.storage.current, limits.storage.max)) : 'primary'}
                        sx={{ mt: 1 }}
                      />
                      {limits && !limits.storage.withinLimit && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Over Limit"
                          color="error"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Features & Limits Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Feature Configuration
                </Typography>
                
                <List>
                  {config && Object.entries(config.features).map(([feature, enabled]) => (
                    <ListItem key={feature}>
                      <ListItemText
                        primary={feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        secondary={`Feature ${enabled ? 'enabled' : 'disabled'} for this salon`}
                      />
                      <ListItemSecondaryAction>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enabled}
                              onChange={(e) => updateFeature(feature, e.target.checked)}
                            />
                          }
                          label=""
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Security & Access Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Settings
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Advanced security and access control settings.
                </Typography>
                <Button 
                  variant="outlined" 
                  href={`/settings?tab=security&salon=${salonId}`}
                  target="_blank"
                >
                  Open Security Settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Notifications Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Configure email, SMS, and push notification preferences.
                </Typography>
                <Button 
                  variant="outlined" 
                  href={`/settings?tab=notifications&salon=${salonId}`}
                  target="_blank"
                >
                  Open Notification Settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Payment Settings Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Configuration
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Payment gateway and billing settings.
                </Typography>
                <Button 
                  variant="outlined" 
                  href={`/settings?tab=pricing&salon=${salonId}`}
                  target="_blank"
                >
                  Open Payment Settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Analytics & Reports Tab */}
      {activeTab === 5 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analytics & Reporting
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Analytics configuration and custom report settings.
                </Typography>
                <Button 
                  variant="outlined" 
                  href={`/settings?tab=analytics&salon=${salonId}`}
                  target="_blank"
                >
                  Open Analytics Settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SalonConfiguration;